// src/layouts/LoggedInLayout/Components/Sidebar/Sidebar.tsx
import React, { useState, useEffect } from 'react';
import { getMyApprovals } from '@/services/PagesServices/CourseApprovalService';
import {
  FiHome,
  FiFileText,
  FiBookOpen,
  FiAward,
  FiHelpCircle,
  FiMessageSquare,
  FiChevronDown,
  FiBarChart2,
  FiPieChart,
  FiCheckSquare,
  FiUsers,
  FiCalendar,
  FiShield,
  FiUserPlus,
  FiSearch,
  FiHeart,
  FiGrid,
  FiBook,
  FiTool,
  FiSettings,
  FiUpload,
  FiBell,
  FiFolder,
  FiClipboard,
  FiTarget,
  FiTrendingUp,
  FiPackage,
  FiVideo,
} from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { CiTrophy } from "react-icons/ci";
import { HiOutlineSparkles } from "react-icons/hi";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/useAuthStore';
import { UserRole } from '../../../constants/roles';
import { OrgFeature, isFeatureAllowed } from '../../../constants/orgFeatures';
import { getMyFeatures } from '@/services/PagesServices/OrgFeaturesService';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  submenu?: MenuItem[];
  active?: boolean;
  isCollapsible?: boolean;
  disabled?: boolean;
  badge?: number;
  /**
   * Org-feature key gating this item (see constants/orgFeatures). When set, the
   * item is only shown if the user's org allows that feature. Untagged items
   * (Dashboard, Account Settings, Support, …) are always visible.
   */
  feature?: string;
}

interface SidebarProps {
  forceExpanded?: boolean; // NEW PROP
}

const Sidebar: React.FC<SidebarProps> = ({ forceExpanded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuthStore();

  const [myLearningsOpen, setMyLearningsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  // Trainer collapsible states
  const [trainerMyLearningsOpen, setTrainerMyLearningsOpen] = useState(false);

  // This decides whether to show text or just icons
  const isSidebarExpanded = forceExpanded || isExpanded;

  // Determine user role
  const userRole = user?.role as UserRole;
  const isAdmin = userRole === UserRole.ADMIN;
  const isTrainee = userRole === UserRole.TRAINEE;
  const isTrainer = userRole === UserRole.TRAINER;
  const isSuperAdmin = userRole === UserRole.SUPER_ADMIN;

  // Pending course-approval count for the "Approvals" sidebar badge (non-learners only).
  const [approvalsCount, setApprovalsCount] = useState(0);
  useEffect(() => {
    if (isTrainee) return;
    let active = true;
    getMyApprovals().then((res) => {
      if (active && res.success) setApprovalsCount((res.data ?? []).length);
    });
    return () => { active = false; };
  }, [isTrainee]);

  // Org-allowed functionalities used to filter the menu. Super admins are never
  // gated (undefined => show everything). For everyone else we seed from the
  // user object (set at login) and refresh from the API so Super-Admin toggle
  // changes take effect without forcing a re-login.
  const allowedFeatures: string[] | undefined = isSuperAdmin ? undefined : user?.allowedFeatures;
  useEffect(() => {
    if (isSuperAdmin || !user) return;
    let active = true;
    getMyFeatures().then((res) => {
      if (!active || !res.success || !res.data) return;
      const next = res.data.allowedFeatures;
      // Only write back if it actually changed (avoid render loops).
      const cur = user.allowedFeatures;
      const changed = !cur || cur.length !== next.length || next.some((k) => !cur.includes(k));
      if (changed) updateUser({ allowedFeatures: next });
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, user?.id]);

  // Recursively drop menu items whose org feature is disabled. Untagged items
  // (and all super-admin items) always pass.
  const filterFeatures = (items: MenuItem[]): MenuItem[] =>
    items
      .filter((item) => isFeatureAllowed(item.feature, allowedFeatures))
      .map((item) =>
        item.submenu ? { ...item, submenu: filterFeatures(item.submenu) } : item
      );

  // Admin Menu Configuration
  const adminMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/admin/dashboard' },
    { label: 'Users', icon: <FiUsers />, path: '/admin/users', feature: OrgFeature.USERS },
    { label: 'My Team', icon: <FiUsers />, path: '/admin/users/team', feature: OrgFeature.TEAMS },
    { label: 'Assignments', icon: <FiClipboard />, path: '/admin/assignments', feature: OrgFeature.ASSIGNMENTS },
    { label: 'Approvals', icon: <FiCheckSquare />, path: '/approvals', badge: approvalsCount, feature: OrgFeature.APPROVALS },
  ];

  const adminContentMenu: MenuItem[] = [
    // { label: 'Content Architect', icon: <FiLayers />, path: '/admin/content/architect' },
    { label: 'Content Management', icon: <FiFileText />, path: '/admin/content/management', feature: OrgFeature.CONTENT_MANAGEMENT },
    { label: 'Course Management', icon: <FiBook />, path: '/admin/content/courses', feature: OrgFeature.COURSES },
    // Bulk Uploader moved here from the (now removed) Tools section.
    { label: 'Bulk Uploader', icon: <FiUpload />, path: '/admin/tools/bulk-uploader', feature: OrgFeature.BULK_UPLOADER },
    { label: 'Catalog / Category', icon: <FiFolder />, path: '/admin/content/catalog', feature: OrgFeature.CATALOG },
    { label: 'Survey and Assessment', icon: <FiClipboard />, path: '/admin/tools/feedback', feature: OrgFeature.ASSESSMENTS },
    { label: 'Group Projects', icon: <FiUsers />, path: '/admin/content/group-projects', feature: OrgFeature.GROUP_PROJECTS },
    { label: 'Certification Management', icon: <FiAward />, path: '/admin/content/certifications', feature: OrgFeature.CERTIFICATIONS },
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/admin/tools/ilt-resources', feature: OrgFeature.ILT },
  ];

  const adminUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/admin/users/team', feature: OrgFeature.TEAMS },
    { label: 'User', icon: <FiUserPlus />, path: '/admin/users/list', feature: OrgFeature.USERS },
  ];

  const adminReportsMenu: MenuItem[] = [
    { label: 'Reports & Analytics', icon: <FiBarChart2 />, path: '/admin/reports', feature: OrgFeature.REPORTS },
  ];

  // The "Tools" section was removed: Bulk Uploader, Survey and Assessment and ILT
  // moved into Learning Content (above); Marketing and Knowledge Base remain as
  // standalone items rendered without a section header.
  const adminStandaloneMenu: MenuItem[] = [
    { label: 'Marketing / Notifications', icon: <FiBell />, path: '/admin/tools/marketing', feature: OrgFeature.MARKETING },
    { label: 'Knowledge Base', icon: <FiBook />, path: '/admin/tools/knowledge-base', feature: OrgFeature.KNOWLEDGE_BASE },
  ];

  const adminSystemMenu: MenuItem[] = [
    { label: 'API Keys', icon: <FiShield />, path: '/admin/settings/api-keys' },
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/admin/support' },
    { label: 'Account Settings', icon: <FiSettings />, path: '/admin/account-settings' },
  ];

  // Trainee Menu Configuration
  const traineeMainMenu: MenuItem[] = [
    { label: 'Home', icon: <FiHome />, path: '/trainee/home' },
    { label: 'Transcript', icon: <FiFileText />, path: '/trainee/transcript' },
    {
      label: 'My Learnings',
      icon: <FiBookOpen />,
      submenu: [
        { label: 'Browse Courses', path: '/trainee/my-learnings/browse-courses' },
        { label: 'My Course', path: '/trainee/my-learnings/my-course' },
        { label: 'Wishlist', path: '/trainee/my-learnings/wishlist' },
      ],
      isCollapsible: true,
    },
  ];

  const traineeLearningsMenu: MenuItem[] = [
    { label: 'Assignments', icon: <FiClipboard />, path: '/learner/assignments', feature: OrgFeature.ASSIGNMENTS },
    { label: 'Achievements', icon: <CiTrophy />, path: '/trainee/achievements' },
    { label: 'Certifications', icon: <FiAward />, path: '/trainee/certifications', feature: OrgFeature.CERTIFICATIONS },
  ];

  // Manager Menu Configuration — rendered as an additional section for ANY
  // role when the logged-in user is the manager of at least one team OR has
  // direct/transitive reportees via users.reports_to_uuid. So an admin who is
  // also a team manager sees their admin menu PLUS Manager Tools, a
  // trainee-manager sees trainee menus PLUS Manager Tools, and a user who
  // only has reportees (no team) still sees Manager Tools so they can review
  // their reportees' submissions.
  const managedTeamIds: number[] = Array.isArray((user as any)?.managedTeamIds)
    ? (user as any).managedTeamIds
    : [];
  const hasReportees = !!(user as any)?.hasReportees;
  const canAccessManager = managedTeamIds.length > 0 || hasReportees;

  const managerMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/manager/dashboard' },
    { label: 'My Teams', icon: <FiUsers />, path: '/manager/teams' },
    { label: 'Reportees', icon: <FiUserPlus />, path: '/manager/reportees' },
    { label: 'Assignments', icon: <FiClipboard />, path: '/manager/assignments' },
  ];

  const traineeToolsMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/trainee/team', feature: OrgFeature.TEAMS },
    { label: 'AI Learning Paths', icon: <FiHelpCircle />, path: '/trainee/ai-paths', feature: OrgFeature.AI_LEARNING_PATHS },
    { label: 'Knowledge Base', icon: <FiFileText />, path: '/knowledge-base', feature: OrgFeature.KNOWLEDGE_BASE },
    { label: 'Support & Help', icon: <FiMessageSquare />, path: '/trainee/support' },
    { label: 'Account Settings', icon: <FiSettings />, path: '/trainee/account-settings' },
  ];

  const traineeFeaturesOnDemand: MenuItem[] = [
    { label: 'Performance', icon: <FiBarChart2 />, path: '/trainee/performance', feature: OrgFeature.PERFORMANCE },
    { label: 'Lingo Lab AI', icon: <HiOutlineSparkles />, path: '/trainee/lingo-lab', feature: OrgFeature.LINGO_LAB },
    { label: 'Carve Assessment', icon: <FiCalendar />, path: '/trainee/assessment', feature: OrgFeature.CARVE_ASSESSMENT },
    { label: 'AI Proctor', icon: <FiShield />, path: '/trainee/proctor', feature: OrgFeature.AI_PROCTOR },
    { label: 'Group Projects', icon: <FiUserPlus />, path: '/trainee/group-projects', feature: OrgFeature.GROUP_PROJECTS },
  ];

  // Trainer Menu Configuration
  const trainerMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/trainer/dashboard' },
    { label: 'Approvals', icon: <FiCheckSquare />, path: '/approvals', badge: approvalsCount, feature: OrgFeature.APPROVALS },
    {
      label: 'My Learnings',
      icon: <FiBookOpen />,
      submenu: [
        { label: 'Browse Course', path: '/trainer/my-learnings/browse-courses' },
        { label: 'My Courses', path: '/trainer/my-learnings/my-courses' },
        { label: 'Wishlist', path: '/trainer/my-learnings/wishlist' },
      ],
    },
  ];

  const trainerContentMenu: MenuItem[] = [
    { label: 'Course Management', icon: <FiBook />, path: '/trainer/content/course-management', feature: OrgFeature.COURSES },
    { label: 'Mini Video', icon: <FiVideo />, path: '/trainer/content/byte-video' },
    { label: 'Assessment', icon: <FiClipboard />, path: '/trainer/content/assessments', feature: OrgFeature.ASSESSMENTS },
    { label: 'Group Project', icon: <FiUsers />, path: '/trainer/content/group-projects', feature: OrgFeature.GROUP_PROJECTS },
    // ILT Resource Management moved here from the (now removed) Tools section.
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/trainer/tools/ilt-resources', feature: OrgFeature.ILT },
  ];

  const trainerUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/trainer/users/my-team', feature: OrgFeature.TEAMS },
    { label: 'User', icon: <FiUserPlus />, path: '/trainer/users/user-list', feature: OrgFeature.USERS },
    { label: 'Learning Assignment Tool', icon: <FiTarget />, path: '/trainer/users/learning-assignment', feature: OrgFeature.ASSIGNMENTS },
    { label: 'Performance', icon: <FiTrendingUp />, path: '/trainer/users/performance', feature: OrgFeature.PERFORMANCE },
  ];

  const trainerReportsMenu: MenuItem[] = [
    { label: 'Reports', icon: <FiBarChart2 />, path: '/trainer/reports', feature: OrgFeature.REPORTS },
  ];

  // The "Tools" section was removed: ILT moved into Learning Content (above);
  // Marketing remains as a standalone item rendered without a section header.
  // (Trainer's Knowledge Base already lives in the System menu.)
  const trainerStandaloneMenu: MenuItem[] = [
    { label: 'Marketing / Notification', icon: <FiBell />, path: '/trainer/tools/marketing', feature: OrgFeature.MARKETING },
  ];

  const trainerSystemMenu: MenuItem[] = [
    { label: 'Knowledge Base', icon: <FiFileText />, path: '/knowledge-base', feature: OrgFeature.KNOWLEDGE_BASE },
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/trainer/support' },
    { label: 'Account Settings', icon: <FiSettings />, path: '/trainer/account-settings' },
  ];

  // Super Admin Menu Configuration
  const superAdminMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/superadmin/dashboard' },
    { label: 'Organization Insights', icon: <FiPieChart />, path: '/superadmin/analytics/organization-insights' },
    { label: 'Approvals', icon: <FiCheckSquare />, path: '/approvals', badge: approvalsCount },
  ];

  const superAdminContentMenu: MenuItem[] = [
    { label: 'Content Management', icon: <FiFileText />, path: '/superadmin/content/content-management' },
    { label: 'Mini Video', icon: <FiVideo />, path: '/superadmin/content/byte-video' },
    { label: 'Course Management', icon: <FiBook />, path: '/superadmin/content/course-management' },
    // Bulk Uploader moved here from the (now removed) Tools section.
    { label: 'Bulk Uploader', icon: <FiUpload />, path: '/superadmin/tools/bulk-uploader' },
    { label: 'Catalog', icon: <FiFolder />, path: '/superadmin/content/catalog' },
    { label: 'Survey and Assessment', icon: <FiMessageSquare />, path: '/superadmin/tools/feedback' },
    { label: 'Group Projects', icon: <FiUsers />, path: '/superadmin/content/group-projects' },
    { label: 'Certification Management', icon: <FiAward />, path: '/superadmin/content/certifications' },
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/superadmin/tools/ilt-resources' },
    { label: 'Training Assistant', icon: <HiOutlineSparkles />, path: '/superadmin/content/training-assistant' },
  ];

  const superAdminUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/superadmin/users/my-team' },
    { label: 'User', icon: <FiUserPlus />, path: '/superadmin/users/user-list' },
    { label: 'Performance Tracking', icon: <FiTrendingUp />, path: '/superadmin/users/performance-tracking' },
    { label: 'Learning Assignment Tool', icon: <FiClipboard />, path: '/superadmin/users/learning-assignment' },
    { label: 'Security and Permissions', icon: <FiShield />, path: '/superadmin/users/security' },
  ];

  // The "Tools" section was removed: Bulk Uploader, Feedback & Survey and ILT
  // moved into Learning Content (above); Marketing and Knowledge Base remain as
  // standalone items rendered without a section header.
  const superAdminStandaloneMenu: MenuItem[] = [
    { label: 'Marketing / Notifications', icon: <FiBell />, path: '/superadmin/tools/marketing' },
    { label: 'Knowledge Base', icon: <FiBook />, path: '/superadmin/tools/knowledge-base' },
  ];

  const superAdminReportsMenu: MenuItem[] = [
    { label: 'Reports & Analytics', icon: <FiBarChart2 />, path: '/superadmin/reports' },
  ];

  const superAdminSystemMenu: MenuItem[] = [
    { label: 'API Keys', icon: <FiShield />, path: '/superadmin/settings/api-keys' },
    { label: 'Organizations', icon: <HiOutlineOfficeBuilding />, path: '/superadmin/settings/organizations' },
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/superadmin/support' },
    { label: 'Account Settings', icon: <FiSettings />, path: '/superadmin/account-settings' },
  ];

  const renderMenuItem = (item: MenuItem, isSubmenu = false) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isActive =
      !!item.path &&
      (location.pathname === item.path ||
        // Treat the index route (/workspace) as active when we are on nested paths like /workspace/
        (item.path === '/workspace' && location.pathname === '/workspace/'));
    const isOpen = item.label === 'My Learnings' ? myLearningsOpen : featuresOpen;
    const showBadge = isSidebarExpanded && !!item.badge && item.badge > 0;

    return (
      <div key={item.label}>
        <div
          className={`logged-in-menu-item ${isActive ? 'logged-in-active' : ''} ${isSubmenu ? 'pl-10' : ''} ${item.disabled ? 'logged-in-disabled' : ''}`}
          onClick={(e) => {
            if (item.disabled) return;
            e.stopPropagation();
            if (item.isCollapsible || hasSubmenu) {
              if (item.label === 'My Learnings') setMyLearningsOpen(!myLearningsOpen);
              if (item.label === 'FEATURES ON DEMAND') setFeaturesOpen(!featuresOpen);
            }
            if (item.path) {
              navigate(item.path);
            }
          }}
          style={{
            justifyContent: hasSubmenu || item.isCollapsible || showBadge ? 'space-between' : 'flex-start',
          }}
        >
          <div className="flex items-center gap-3">
            {!isSubmenu && item.icon}
            <span className="logged-in-menu-item-text">{item.label}</span>
          </div>

          {showBadge && (
            <span
              style={{
                background: '#e5484d',
                color: '#fff',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600,
                padding: '1px 7px',
                minWidth: '18px',
                textAlign: 'center',
                lineHeight: '16px',
              }}
            >
              {item.badge! > 99 ? '99+' : item.badge}
            </span>
          )}

          {(hasSubmenu || item.isCollapsible) && (
            <FiChevronDown
              className={`logged-in-dropdown-arrow ${isOpen ? 'logged-in-open' : ''}`}
            />
          )}
        </div>

        {/* Render actual submenu if exists */}
        {hasSubmenu && isOpen && (
          <div className="logged-in-submenu logged-in-open">
            {item.submenu!.map((sub) => renderMenuItem(sub, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .logged-in-sidebar {
          background-color: var(--color-white);
          overflow-y: auto;
          border-radius: 1rem;
          padding: 1.5rem 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          height: 100%;
          transition: all 0.3s ease;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
          [data-theme="dark"] .logged-in-sidebar {
            background-color: var(--color-primary-dark);
            border:none;
          }
        
        .logged-in-sidebar::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
        
        .logged-in-sidebar.expanded {
          padding: 1.5rem 1rem;
        }
        
        .logged-in-menu-title {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 0 8px;
          margin-bottom: 16px;
          display: block;
          white-space: nowrap;
          text-align: center;
          transition: all 0.3s ease;
        }
          [data-theme="dark"] .logged-in-menu-title { color: var(--color-grey); }
        
        .logged-in-sidebar.expanded .logged-in-menu-title {
          font-size: 13px;
          text-align: left;
          margin-bottom: 24px;
        }
        
        .logged-in-menu-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          margin-bottom: 4px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          color: #4b5563;
          font-size: 13px;
          position: relative;
        }
          [data-theme="dark"] .logged-in-menu-item { color: var(--color-grey) }

          .logged-in-menu-item:hover{
          color: var(-color-black);
          }
        [data-theme="dark"] .logged-in-menu-item:hover {
        color: var(--color-white);
        }

        .logged-in-menu-item.logged-in-disabled {
          cursor: default;
          opacity: 0.5;
          pointer-events: none;
        }

        .logged-in-menu-item.logged-in-disabled:hover {
          background-color: transparent;
          color: #4b5563;
        }

        [data-theme="dark"] .logged-in-menu-item.logged-in-disabled:hover {
          background-color: transparent;
          color: var(--color-grey);
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item {
          justify-content: center;
          padding: 12px 8px;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item .flex {
          justify-content: center;
          width: 100%;
        }
        
        .logged-in-menu-item.logged-in-active {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
          [data-theme="dark"] .logged-in-menu-item.logged-in-active {
            background-color: var(--color-sunflare);
            color: var(--color-white);
          }
        
        .logged-in-menu-item svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        
        .logged-in-menu-item-text {
          white-space: nowrap;
          overflow: hidden;
          transition: opacity 0.3s ease, width 0.3s ease;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item-text {
          opacity: 0;
          width: 0;
        }
        
        .logged-in-dropdown-arrow {
          width: 14px;
          height: 14px;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-dropdown-arrow {
          opacity: 0;
          width: 0;
        }
        
        .logged-in-dropdown-arrow.logged-in-open {
          transform: rotate(180deg);
        }
        
        .logged-in-section-title {
          font-size: 10px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin: 20px 0 8px 0;
          padding: 0 8px;
          white-space: nowrap;
          text-align: center;
          transition: all 0.3s ease;
        }
        
        .logged-in-sidebar.expanded .logged-in-section-title {
          font-size: 12px;
          text-align: left;
          margin: 24px 0 12px 16px;
          letter-spacing: 0.5px;
        }
        
        .logged-in-submenu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease;
        }
        
        .logged-in-submenu.logged-in-open {
          max-height: 1000px;
        }
        
        .pl-10 {
          padding-left: 40px !important;
          font-size: 12px !important;
        }
        
        .logged-in-sidebar:not(.expanded) .pl-10 {
          padding-left: 12px !important;
        }
        
        .logged-in-menu-item .flex {
          gap: 12px !important;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item .flex {
          gap: 0 !important;
        }
            @media (max-width: 500px) {
   
        .logged-in-menu-item {
          font-size: 12px;
        }

           
        .logged-in-dropdown-arrow {
          width: 10px;
          height: 10px;
        }
          }
      `}</style>

   <div
        className={`logged-in-sidebar ${isSidebarExpanded ? 'expanded' : ''}`}
        onMouseEnter={() => !forceExpanded && setIsExpanded(true)}
        onMouseLeave={() => {
          if (!forceExpanded) {
            setIsExpanded(false);
            setMyLearningsOpen(false);
          }
        }}
      >
        {/* Render Admin Sidebar */}
        {isAdmin && (
          <>
            {filterFeatures(adminMainMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Learning Content' : 'Content'}
            </div>
            {filterFeatures(adminContentMenu).map(item => renderMenuItem(item))}

            {filterFeatures(adminStandaloneMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Settings' : 'Sys'}
            </div>
            {/* Only show Account Settings from admin system menu */}
            {adminSystemMenu
              .filter(item => item.label === 'Account Settings')
              .map(item => renderMenuItem(item))
            }
          </>
        )}

        {/* Render Trainee Sidebar */}
        {isTrainee && (
          <>
            <span className="logged-in-menu-title">
              {isSidebarExpanded ? 'Main Menu' : 'Menu'}
            </span>

            {filterFeatures(traineeMainMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Learnings' : 'Learn'}
            </div>
            {filterFeatures(traineeLearningsMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">Tools</div>
            {filterFeatures(traineeToolsMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Features on Demand' : 'Features'}
            </div>
            {filterFeatures(traineeFeaturesOnDemand).map(item => renderMenuItem(item))}
          </>
        )}

        {/* Render Trainer Sidebar */}
        {isTrainer && (
          <>
            {filterFeatures(trainerMainMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Learning Content' : 'Content'}
            </div>
            {filterFeatures(trainerContentMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'User Management' : 'Users'}
            </div>
            {filterFeatures(trainerUserMenu).map(item => renderMenuItem(item))}

            {filterFeatures(trainerReportsMenu).map(item => renderMenuItem(item))}
            {filterFeatures(trainerStandaloneMenu).map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'System' : 'Sys'}
            </div>
            {filterFeatures(trainerSystemMenu).map(item => renderMenuItem(item))}
          </>
        )}

        {/* Render Super Admin Sidebar */}
        {isSuperAdmin && (
          <>
            {superAdminMainMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Learning Content' : 'Content'}
            </div>
            {superAdminContentMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'User Management' : 'Users'}
            </div>
            {superAdminUserMenu.map(item => renderMenuItem(item))}

            {superAdminReportsMenu.map(item => renderMenuItem(item))}
            {superAdminStandaloneMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'System' : 'Sys'}
            </div>
            {superAdminSystemMenu.map(item => renderMenuItem(item))}
          </>
        )}

        {/* Manager Tools — rendered for ANY role when the user is the manager
            of at least one team. Lives outside the role-specific blocks so an
            admin who is also a team manager sees their admin menu PLUS this. */}
        {canAccessManager && (
          <>
            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Manager Tools' : 'Mgr'}
            </div>
            {managerMenu.map(item => renderMenuItem(item))}
          </>
        )}
      </div>
    </>
  );
};

export default Sidebar;